import type { Control } from 'react-hook-form';
import cx from 'classnames';
import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { ControlledFormCurrencyInput } from '@components/formInputs/ControlledFormCurrencyInput';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import { LayoutVariant } from '@components/formInputs/sharedTypes';
import type { EventFormData } from '../RestaurantEventForm';
import { IconButton } from '@components/iconButton/IconButton';
import { useHtmlId } from '@shared/hooks/useHtmlId';
import styles from './TicketTierRow.scss';

export const TicketTierRow = ({
  index,
  control,
  onOpenAssignTables,
  onRemove,
}: {
  index: number;
  control: Control<EventFormData>;
  onOpenAssignTables: (index: number) => void;
  onRemove: (index: number) => void;
}) => {
  const assignTablesTooltipId = useHtmlId('tier-assign-tables');
  const removeTierTooltipId = useHtmlId('tier-remove');

  return (
    <div className={styles.ticketTierRow}>
      <div className={styles.ticketTierFieldsRow}>
        <ControlledFormInput<EventFormData>
          control={control}
          name={`ticketTiers.${index}.name`}
          type="text"
          label="Tier name"
          labelClassName={styles.fieldLabel}
          darkMode
        />
        <ControlledFormCurrencyInput<EventFormData>
          control={control}
          name={`ticketTiers.${index}.price`}
          label="Price"
          labelClassName={styles.fieldLabel}
          darkMode
          integerDollarsOnly
          rules={{ required: 'Price is required' }}
        />
        <ControlledFormInput<EventFormData>
          control={control}
          name={`ticketTiers.${index}.capacity`}
          type="number"
          label="Capacity"
          labelClassName={styles.fieldLabel}
          darkMode
          rules={{ required: 'Capacity is required' }}
        />
        <ControlledFormInput<EventFormData>
          control={control}
          aria-label="Table count"
          name={`ticketTiers.${index}.tableCount`}
          type="number"
          label="Table count"
          labelClassName={styles.fieldLabel}
          readOnly
          darkMode
        />
        <IconButton
          ariaLabel="Assign tables"
          className={cx(
            styles.eventModalActionIconButton,
            styles.eventModalActionIconButtonDefaultStroke,
            styles.tierActionButton,
            styles.iconButtonDefaultStroke,
          )}
          dataTooltipId={assignTablesTooltipId}
          iconName="table"
          onClick={() => onOpenAssignTables(index)}
          tooltipContent="Assign tables"
        />
        {index !== 0 && (
          <IconButton
            ariaLabel="Remove"
            className={cx(
              styles.eventModalActionIconButton,
              styles.eventModalActionIconButtonDefaultStroke,
              styles.tierActionButton,
              styles.iconButtonDefaultStroke,
            )}
            dataTooltipId={removeTierTooltipId}
            iconName="trash"
            onClick={() => onRemove(index)}
            tooltipContent="Remove"
          />
        )}
      </div>

      <div className={styles.ticketTierDescriptionField}>
        <ControlledFormTextareaAutosize<EventFormData>
          control={control}
          name={`ticketTiers.${index}.description`}
          label="Description (optional)"
          placeholder="Enter a description for this tier..."
          minRows={1}
          maxRows={2}
          variant={LayoutVariant.Vertical}
          labelClassName={styles.fieldLabel}
          ariaLabel="Tier description"
          darkMode
        />
      </div>
    </div>
  );
};
